// Root component for the Scan-App. Dark, mobile-first, localized customer UI.
//
// Phases (local state, no router):
//   "landing"    → no token — random visitor / lost link
//   "loading"    → fetching the scan session
//   "invalid"    → bad/expired token (reason kind, localized in the screen)
//   "ready"      → session loaded → capture flow (self-capture camera UI, or
//                  the volumental OBJ file-picker)
//   "running"    → upload in progress
//   "incomplete" → the upload finished with at least one file still missing
//                  from S3. Terminal for the pipeline, NOT for the scan: the
//                  blobs are still in memory, so the screen offers a retry
//                  that re-uploads exactly the failed files. No trigger fires
//                  from here, since footscan-server would list a short prefix.
//   "triggering" → all PUTs done, footscan-server process-trigger in flight
//   "between"    → a run of a multi-run invite is in (uploaded, notified,
//                  triggered) and the picked pairs need another one: a short
//                  "run k of N done, set up again" page, then the next /start.
//                  Never says "done" or "close the page".
//   "polling"    → trigger answered 202 (accepted); polling GET .../status
//                  every 3s for the real outcome instead of holding a
//                  ~150s request open. Renders the same finished screen as
//                  "done" and caps at ~4 minutes, after which the customer
//                  keeps the "we'll email you" wording for good.
//   "done"       → a terminal outcome: sync success, a resolved async
//                  poll (ok or failed), or the polling cap reached
//   "failed"     → a critical step blew up
//
// Operator/technical detail (scanID, S3 keys, per-file list, trigger internals)
// is DEBUG-gated — customers see friendly, localized screens only.

import { useEffect, useRef, useState } from "react";

import { CaptureFlow, type PaperFormat } from "./components/CaptureFlow";
import { HezoLogo } from "./components/HezoLogo";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { PairPicker } from "./components/PairPicker";
import { UploadList } from "./components/UploadList";
import {
  ApiError,
  fetchScanSession,
  fetchScanStatus,
  isRecoverableTriggerError,
  notifyScanSession,
  startScanSession,
  statusForFailureReason,
  triggerScanProcess,
  type ProcessTriggerResult,
  type ScanIssue,
  type ScanPair,
  type ScanSession,
  type SessionResponse,
} from "./lib/api";
import {
  APP_VERSION,
  CAPTURE_INGEST_URL,
  DEBUG,
  DEV_SESSION,
  HEZO_OPS_BASE_URL,
  captureIngestToken,
  devCaptureEnabled,
  tokenFromLocation,
} from "./lib/config";
import {
  buildScanBundle,
  downloadJson,
  normaliseCaptureLabel,
  postScanBundle,
  type IngestOutcome,
} from "./lib/local-save";
import { useI18n, type TransKey } from "./lib/i18n";
import {
  fakeFilesToInput,
  runUploadPipeline,
  type RunInput,
  type UploadItem,
} from "./lib/upload-pipeline";
import { buildFullScanPayload } from "./lib/fake-content";
import { isOptionalUpload } from "./lib/capture";
import { createFunnelReporter } from "./lib/telemetry";
import {
  VOLUMENTAL_ACCEPT,
  volumentalExtension,
  volumentalInputs,
  volumentalTargetName,
  type VolumentalSide,
} from "./lib/volumental";
import { loadRunConsent, storeRunConsent } from "./lib/run-consent";
import {
  openRunPairs,
  planInviteRuns,
  planRuns,
  recordRun,
  runInfo,
  startPairIds,
  startedRunPairs,
  syncInviteRunPlan,
  syncRunPlan,
  type RunInfo,
  type RunPlan,
} from "./lib/scan-runs";

export type InvalidReason = "expired" | "notFound" | "unreachable" | "processed";

// Reusable multi-pair state: the invite's open pairs plus the transient
// picker UI state (whether /start is in flight, and any surfaced error).
interface PickingState {
  customerId: string;
  flow: ScanSession["flow"];
  pairs: ScanPair[];
  starting: boolean;
  error?: string;
}

type Phase =
  | { kind: "landing" }
  // Dev capture (`?dev=1`) finished and the result went to a file — or, with a
  // capture-ingest token, straight to the ingest. Distinct from "done" on
  // purpose: nothing was uploaded to S3 and no backend was triggered, so reusing
  // that phase would claim a success that never happened. `ingest` is null when
  // no token was set (today's plain-download behaviour).
  | {
      kind: "savedLocally";
      session: ScanSession;
      filename: string | null;
      ingest: IngestOutcome | null;
    }
  | { kind: "loading" }
  | { kind: "invalid"; reason: InvalidReason; detail?: string }
  | { kind: "allDone" }
  | { kind: "picking"; picking: PickingState }
  | {
      kind: "between";
      customerId: string;
      flow: ScanSession["flow"];
      /** The run just finished, and the one the button starts. */
      finished: RunInfo;
      next: RunInfo;
      pairIds: string[];
      starting: boolean;
      error?: string;
    }
  | { kind: "ready"; session: ScanSession }
  | { kind: "running"; session: ScanSession; items: UploadItem[] }
  | { kind: "incomplete"; session: ScanSession; items: UploadItem[] }
  | { kind: "triggering"; session: ScanSession; items: UploadItem[] }
  | {
      kind: "polling";
      session: ScanSession;
      items: UploadItem[];
      expectedSeconds: number;
    }
  | {
      kind: "done";
      session: ScanSession;
      items: UploadItem[];
      triggerResult: ProcessTriggerResult;
    }
  | {
      kind: "failed";
      session: ScanSession;
      items: UploadItem[];
      errorMessage: string;
      /** HTTP status of the step that blew up; 0 for a network error or a
       *  throw with no response. Decides whether a retry can help and which
       *  copy the customer reads. */
      status: number;
    };

// Shared dark panel + button classes, so every screen matches the immersive
// capture UI.
const PANEL = "rounded-xl border border-white/10 bg-white/5 p-4";
const PRIMARY_BTN =
  "w-full rounded-full bg-white py-3.5 text-base font-semibold text-slate-900 shadow-lg transition active:bg-slate-100 disabled:opacity-40";
const SECONDARY_BTN =
  "w-full rounded-full border border-white/15 bg-transparent py-3 text-sm font-medium text-slate-200 transition hover:bg-white/5";

// Map a failed GET /scan-sessions into the right "invalid" screen. 401 →
// expired/revoked, 410 → already-processed/orphaned, 404 → not found,
// anything else (network, 5xx) → generic unreachable.
// A reusable invite that covers no pair (hezo-ops PR C: walk-in, campaign,
// complaint, API): open, but nothing to pick. Its runs count per invite.
function isPairlessInvite(res: SessionResponse): boolean {
  return res.mode === "multi-pair" && !res.done && res.pairs.length === 0;
}

function invalidPhaseFromError(err: unknown): Phase {
  if (err instanceof ApiError) {
    if (err.status === 401) return { kind: "invalid", reason: "expired" };
    if (err.status === 410) return { kind: "invalid", reason: "processed" };
    if (err.status === 404) return { kind: "invalid", reason: "notFound" };
  }
  return { kind: "invalid", reason: "unreachable", detail: (err as Error).message };
}

// The initial, nothing-uploaded-yet item list for a run.
function pendingItems(inputs: RunInput[]): UploadItem[] {
  return inputs.map((inp, i) => ({
    id: `${inp.filename}#${i}`,
    filename: inp.filename,
    size: inp.blob.size,
    contentType: inp.contentType,
    status: "pending" as const,
  }));
}

// Fold a partial run's items back into the full list. `indices[k]` is the slot
// `fresh[k]` came from; the original `id` is kept so React doesn't remount the
// row (the pipeline numbers ids by its own input index, which a retry shifts).
function mergeItems(
  base: UploadItem[],
  indices: number[],
  fresh: UploadItem[],
): UploadItem[] {
  const next = base.slice();
  fresh.forEach((it, k) => {
    const target = indices[k];
    if (target === undefined) return;
    next[target] = { ...it, id: next[target].id };
  });
  return next;
}

// Localised message for a failed POST /start, keyed off the contract's error
// statuses (400 not_reusable_invite, 409 invalid_pairs, 401, 410).
function startErrorMessage(err: unknown, t: ReturnType<typeof useI18n>["t"]): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return t("picker.errorNotReusable");
    if (err.status === 409) return t("picker.errorInvalidPairs");
    if (err.status === 401) return t("picker.errorExpired");
    if (err.status === 410) return t("picker.errorProcessed");
  }
  return t("picker.errorGeneric", { message: (err as Error).message });
}

export function App(): React.ReactElement {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [volumentalFiles, setVolumentalFiles] = useState<VolumentalFiles>({
    left: null,
    right: null,
  });
  const tokenRef = useRef<string | null>(null);
  // True once the customer entered via a reusable multi-pair invite (GET →
  // picker → /start). It prepends hezo-ops's /notify (pair attribution) to the
  // footscan-server /process trigger. Legacy invites leave it false, so their
  // path is byte-for-byte unchanged.
  const reusableRef = useRef(false);
  // The session-scoped scan JWT from /start. footscan-server's /process and
  // /status reject the invite token (no `scanId` claim), so a reusable session
  // talks to footscan-server with this one. Null on legacy invites, which
  // already hold a per-scan token in `tokenRef`.
  const scanTokenRef = useRef<string | null>(null);
  // scanID whose /notify already landed. A DoneScreen retry after a failed
  // /process must not re-send it: hezo-ops answers a replay with 410.
  const notifiedScanRef = useRef<string | null>(null);
  // The blobs of the run in flight, kept for the retry paths. A partial upload
  // failure ("incomplete") and a hard failure ("failed") both re-PUT from here
  // instead of sending the customer back through the capture flow. The photos
  // are the expensive part, the HTTP call is not.
  const uploadInputsRef = useRef<RunInput[]>([]);
  // Multi-run invites (hezo-ops § Phase 3): the visit's view of how many runs
  // each picked pair has, and the pairs the run in flight is for. Null when
  // the invite asks for a single run or predates run counts, which keeps that
  // path exactly as it was.
  const runPlanRef = useRef<RunPlan | null>(null);
  const runPairsRef = useRef<string[]>([]);
  // Paper format from run 1's consent screen. Later runs skip consent and
  // start at the prep checklist with the same format.
  const consentRef = useRef<{ paperFormat: PaperFormat } | null>(null);
  // The camera ran in this page already, so the motion permission from that
  // tap holds: later runs start the camera without the checklist screen. A
  // reopened link has not had that tap yet and shows one short screen.
  const cameraStartedRef = useRef(false);
  // "Durchlauf k von N" of the run in flight, for the viewfinder and the
  // upload screens. Null on single-run scans.
  const [run, setRun] = useState<RunInfo | null>(null);
  // Set after the last run of the picked pairs when the invite still has other
  // pairs open (a second person on the order): the done screen then offers the
  // picker instead of "close the page".
  const [morePairs, setMorePairs] = useState(false);
  // The picked pairs of a multi-run round, and the summary the done screen
  // shows after its last run instead of that one run's scanID.
  const pickedPairsRef = useRef<ScanPair[]>([]);
  const [runsSummary, setRunsSummary] = useState<RunsSummary | null>(null);
  // Campaign funnel telemetry. Fire-and-forget by construction: no await, no
  // result, no error surface, so a broken endpoint cannot stall the capture
  // flow. Created once per page load (the reporter carries the "already sent"
  // set) and stable, so children can list it in effect deps.
  const [emit] = useState(() =>
    createFunnelReporter(HEZO_OPS_BASE_URL, () => tokenRef.current),
  );

  useEffect(() => {
    const token = tokenFromLocation();
    tokenRef.current = token;
    if (!token) {
      // No invite. Normally that is the landing screen — but dev capture
      // deliberately runs the real capture UI against a synthetic session so
      // camera/guidance/telemetry work needs no minted invite.
      if (devCaptureEnabled()) {
        setPhase({ kind: "ready", session: { ...DEV_SESSION } });
        return;
      }
      setPhase({ kind: "landing" });
      return;
    }
    void (async () => {
      try {
        const res = await fetchScanSession(HEZO_OPS_BASE_URL, token);
        if (res.mode === "multi-pair") {
          reusableRef.current = true;
          if (res.done) {
            setPhase({ kind: "allDone" });
          } else if (isPairlessInvite(res)) {
            await startPairless(token, res);
          } else {
            await pickOrResume(token, res);
          }
        } else {
          reusableRef.current = false;
          // Funnel step 1. A single-pair (and therefore every campaign) invite
          // carries its scanID in the token, so the first step is reportable
          // from the very first page view.
          emit("session_loaded", res.scanID);
          setPhase({
            kind: "ready",
            session: { customerId: res.customerId, scanID: res.scanID, flow: res.flow },
          });
        }
      } catch (err) {
        setPhase(invalidPhaseFromError(err));
      }
    })();
  }, []);

  // Warn on tab close or navigation while the scan can still be lost: during
  // the upload / process-trigger (leaving aborts in-flight PUTs or drops the
  // trigger), and on the two recoverable failure screens, whose retry buttons
  // re-PUT the blobs held in `uploadInputsRef`, blobs a reload would destroy.
  // The browser shows its own generic "Leave site?" prompt (custom text is
  // ignored by modern browsers), so we only need to opt in. Removed as soon as
  // we reach a phase where nothing is left to lose.
  useEffect(() => {
    if (
      phase.kind !== "running" &&
      phase.kind !== "triggering" &&
      phase.kind !== "incomplete" &&
      phase.kind !== "failed"
    )
      return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase.kind]);

  // Poll for the real outcome of an accepted (202) trigger. Deliberately NOT
  // gated by `beforeunload` above: the reconstruction runs server-side now,
  // so a locked phone or a closed tab no longer aborts anything: the
  // customer just resumes seeing progress (or the eventual email) whenever
  // they come back. `startedAt` is a wall-clock timestamp rather than a tick
  // counter so a suspended tab's throttled timers still measure real elapsed
  // time correctly once resumed, and a `visibilitychange` listener re-polls
  // immediately on resume instead of waiting out a stale 3s timer.
  useEffect(() => {
    if (phase.kind !== "polling") return;
    const { session, items, expectedSeconds } = phase;
    const pollToken = scanTokenRef.current ?? tokenRef.current;
    if (!pollToken) return;
    const jwt: string = pollToken;

    const POLL_MS = 3000;
    const CAP_MS = 4 * 60 * 1000;
    const startedAt = Date.now();
    let cancelled = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function settleOk(status: { outputCount?: number }): void {
      setPhase({
        kind: "done",
        session,
        items,
        triggerResult: {
          kind: "ok",
          status: 200,
          // The poll saw the reconstruction finish without errors, so the done
          // screen may say the photos are processed and awaiting our check,
          // instead of promising to build a model that is already built.
          processed: true,
          body: { ok: true, scanID: session.scanID, outputCount: status.outputCount },
        },
      });
    }

    // Processing ran and failed. `stage: "processing"` is what DoneScreen keys
    // its copy off. The trigger itself did get through here, so the
    // "not fully submitted" wording of the trigger-failure case is wrong. The
    // status mirrors what the server answers for the same failure class on its
    // sync path, so an `internal_error` stays retryable instead of being
    // flattened into a deterministic 422 (see `statusForFailureReason`).
    function settleFailed(issues: ScanIssue[], reason: string | undefined): void {
      setPhase({
        kind: "done",
        session,
        items,
        triggerResult: {
          kind: "error",
          stage: "processing",
          status: statusForFailureReason(reason),
          body: issues[0]?.message ?? "processing failed",
          customerIssues: issues,
        },
      });
    }

    // The cap reached without a resolved status: fall back to the original
    // "accepted" outcome so DoneScreen shows its existing async-success
    // copy (customer keeps their photos + gets an email once it's done).
    function settleTimedOut(): void {
      setPhase({
        kind: "done",
        session,
        items,
        triggerResult: {
          kind: "accepted",
          status: 202,
          body: { ok: true, scanID: session.scanID, expectedSeconds },
        },
      });
    }

    async function tick(): Promise<void> {
      if (cancelled || inFlight) return;
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs >= CAP_MS) {
        settleTimedOut();
        return;
      }
      inFlight = true;
      const status = await fetchScanStatus(
        HEZO_OPS_BASE_URL,
        jwt,
        session.scanID,
        session.customerId,
      );
      inFlight = false;
      if (cancelled) return;
      if (status.kind === "ok") {
        settleOk(status);
        return;
      }
      if (status.kind === "failed") {
        settleFailed(status.issues, status.reason);
        return;
      }
      timer = setTimeout(() => void tick(), POLL_MS);
    }

    function onVisibilityChange(): void {
      if (document.visibilityState !== "visible") return;
      if (timer) clearTimeout(timer);
      void tick();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // Only (re)start when polling begins for a given scan. The effect owns its
    // own elapsed-time bookkeeping, so nothing it does re-runs it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind === "polling" ? phase.session.scanID : undefined]);

  // Reusable flow: customer picked ≥1 pair → POST /start opens a ScanSession,
  // then we hand off to the same capture/volumental flow as the legacy path.
  async function startPicking(pairIds: string[]): Promise<void> {
    if (phase.kind !== "picking") return;
    const token = tokenRef.current;
    if (!token || pairIds.length === 0) return;
    const picking = phase.picking;
    setPhase({ kind: "picking", picking: { ...picking, starting: true, error: undefined } });
    pickedPairsRef.current = picking.pairs.filter((p) => pairIds.includes(p.id));
    runPlanRef.current = planRuns(pickedPairsRef.current);
    try {
      await openSession(token, pairIds, picking.customerId, picking.flow);
    } catch (err) {
      // 409: the server sees the pairs differently (a run sequence under way
      // pins the selection, or the counts moved): re-read and continue there.
      if (err instanceof ApiError && err.status === 409) {
        await resumeInvite();
        return;
      }
      setPhase({
        kind: "picking",
        picking: { ...picking, starting: false, error: startErrorMessage(err, t) },
      });
    }
  }

  // POST /start for one run and hand off to the capture flow. Throws the
  // /start error; the caller decides which screen shows it.
  async function openSession(
    token: string,
    pairIds: string[],
    customerId: string,
    flow: ScanSession["flow"],
  ): Promise<void> {
    const { scanID, scanToken } = await startScanSession(
      HEZO_OPS_BASE_URL,
      token,
      startPairIds(runPlanRef.current, pairIds),
    );
    scanTokenRef.current = scanToken ?? null;
    // Funnel step 1 for reusable invites. Deliberately later than on the
    // single-pair path: before /start the token carries only an inviteId, so
    // there is no scanID for the event to attach to yet.
    emit("session_loaded", scanID);
    runPairsRef.current = pairIds;
    const plan = runPlanRef.current;
    setRun(plan ? runInfo(plan, pairIds) : null);
    setMorePairs(false);
    setVolumentalFiles({ left: null, right: null });
    setPhase({ kind: "ready", session: { customerId, scanID, flow } });
  }

  // A pairless invite skips the picker: plan its runs from the invite-level
  // count and open the first one straight away, like a single-scan link
  // lands in the capture flow. A 409 means its runs are all in meanwhile.
  async function startPairless(
    token: string,
    res: Extract<SessionResponse, { mode: "multi-pair" }>,
  ): Promise<void> {
    const plan = planInviteRuns(res);
    runPlanRef.current = plan;
    try {
      await openSession(token, plan ? plan.pairIds : [], res.customerId, res.flow);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        runPlanRef.current = null;
        setPhase({ kind: "allDone" });
        return;
      }
      setPhase(invalidPhaseFromError(err));
    }
  }

  // Consent for run 2+: given in this visit, or stored on this phone by run 1
  // of an earlier visit to the same link. Null sends the run through consent.
  function runConsent(): { paperFormat: PaperFormat } | null {
    if (consentRef.current) return consentRef.current;
    const stored = tokenRef.current ? loadRunConsent(tokenRef.current) : null;
    if (stored) consentRef.current = stored;
    return stored;
  }

  // A multi-pair invite with pairs open: the picker, unless a run sequence is
  // under way. Then its pairs continue straight away, the selection of run 1
  // is fixed (hezo-ops refuses another one with 409 `resume_required`).
  async function pickOrResume(
    token: string,
    res: Extract<SessionResponse, { mode: "multi-pair" }>,
  ): Promise<void> {
    const started = startedRunPairs(res.pairs);
    const plan = started.length > 0 ? planRuns(started) : null;
    if (!plan) {
      setPhase({
        kind: "picking",
        picking: { customerId: res.customerId, flow: res.flow, pairs: res.pairs, starting: false },
      });
      return;
    }
    pickedPairsRef.current = started;
    runPlanRef.current = plan;
    try {
      await openSession(token, plan.pairIds, res.customerId, res.flow);
    } catch (err) {
      runPlanRef.current = null;
      setPhase(invalidPhaseFromError(err));
    }
  }

  // The "next run" button: a fresh /start for the pairs that still need one.
  // A 409 means the server sees the pairs differently (a run failed or the
  // operator bound one meanwhile), so the picker with fresh counts takes over.
  async function startNextRun(): Promise<void> {
    if (phase.kind !== "between") return;
    const token = tokenRef.current;
    if (!token) return;
    const between = phase;
    setPhase({ ...between, starting: true, error: undefined });
    try {
      await openSession(token, between.pairIds, between.customerId, between.flow);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await resumeInvite();
        return;
      }
      setPhase({ ...between, starting: false, error: startErrorMessage(err, t) });
    }
  }

  // Re-read the invite and continue where the server says it stands: the
  // picker with the pairs still open, or the all-done screen. Used to scan the
  // other pairs of an order after the last run, and after a failed run.
  async function resumeInvite(): Promise<void> {
    const token = tokenRef.current;
    if (!token) return;
    runPlanRef.current = null;
    setRun(null);
    setMorePairs(false);
    setRunsSummary(null);
    try {
      const res = await fetchScanSession(HEZO_OPS_BASE_URL, token);
      if (res.mode !== "multi-pair" || res.done) {
        setPhase({ kind: "allDone" });
        return;
      }
      if (isPairlessInvite(res)) {
        await startPairless(token, res);
        return;
      }
      await pickOrResume(token, res);
    } catch (err) {
      setPhase(invalidPhaseFromError(err));
    }
  }

  // A run of a multi-run invite got through (/notify landed, /process
  // accepted). Count it, preferably from a fresh GET, and say which pairs the
  // next run is for. An empty list means the picked pairs have all their runs.
  async function advanceRuns(
    plan: RunPlan,
    token: string,
  ): Promise<{ next: string[]; plan: RunPlan; otherPairsOpen: boolean }> {
    try {
      const res = await fetchScanSession(HEZO_OPS_BASE_URL, token);
      if (res.mode === "multi-pair" && plan.pairless) {
        const synced = syncInviteRunPlan(plan, res);
        return { next: openRunPairs(synced), plan: synced, otherPairsOpen: false };
      }
      if (res.mode === "multi-pair") {
        const synced = syncRunPlan(plan, res.pairs);
        return {
          next: openRunPairs(synced),
          plan: synced,
          otherPairsOpen: res.pairs.some((p) => !plan.pairIds.includes(p.id)),
        };
      }
    } catch {
      // Fall through to the local count: the run's /notify landed, so it
      // counts unless processing fails later, and then the link offers it again.
    }
    const counted = recordRun(plan, runPairsRef.current);
    return { next: openRunPairs(counted), plan: counted, otherPairsOpen: false };
  }

  // DEBUG-only fallback used by CaptureFlow's "simulate" button.
  async function startSelfCaptureUpload(label?: string): Promise<void> {
    if (phase.kind !== "ready") return;
    const fakeFiles = await buildFullScanPayload();
    await runScanUpload(fakeFilesToInput(fakeFiles), label);
  }

  async function startVolumentalUpload(files: VolumentalFiles): Promise<void> {
    if (phase.kind !== "ready" || !files.left || !files.right) return;
    await runScanUpload(volumentalInputs(files.left, files.right));
  }

  async function runScanUpload(inputs: RunInput[], label?: string): Promise<void> {
    if (phase.kind !== "ready") return;
    const session = phase.session;
    const token = tokenRef.current;
    // Dev capture has no JWT, so no presigned URLs and nowhere to PUT. Save the
    // exact byte set the pipeline would have uploaded — capture-meta.json
    // included — and stop there. `label` is a dev-capture note and is dropped
    // on the real upload path below, where the scanID identifies the run.
    if (!token) {
      if (devCaptureEnabled()) await saveScanLocally(session, inputs, label);
      return;
    }

    uploadInputsRef.current = inputs;
    const items = pendingItems(inputs);
    setPhase({ kind: "running", session, items });
    await uploadAndSettle(
      session,
      token,
      items,
      inputs.map((_, i) => i),
    );
  }

  // Upload one slice of `uploadInputsRef.current` and take the run to its next
  // phase. `indices` selects which inputs to PUT (every one on the first run,
  // only the failed ones on a retry) and doubles as the map back onto the full
  // item list, since the pipeline keeps its inputs' order.
  //
  // The completion trigger fires only when every single item is "done".
  // footscan-server processes whatever it finds under the scan's S3 prefix, so
  // triggering on a short prefix produces a confidently wrong reconstruction
  // rather than an error. The customer must re-send the missing files first.
  async function uploadAndSettle(
    session: ScanSession,
    token: string,
    baseItems: UploadItem[],
    indices: number[],
  ): Promise<void> {
    const allInputs = uploadInputsRef.current;
    const inputs = indices.map((i) => allInputs[i]);
    let result: Awaited<ReturnType<typeof runUploadPipeline>>;
    try {
      result = await runUploadPipeline({
        baseUrl: HEZO_OPS_BASE_URL,
        token,
        scanID: session.scanID,
        inputs,
        concurrency: 4,
        onProgress: (ev) => {
          setPhase((prev) =>
            prev.kind === "running"
              ? { ...prev, items: mergeItems(baseItems, indices, ev.items) }
              : prev,
          );
        },
      });
    } catch (err) {
      setPhase({
        kind: "failed",
        session,
        items: baseItems,
        errorMessage:
          err instanceof ApiError
            ? `Could not request presigned uploads: ${err.status}: ${err.body}`
            : (err as Error).message,
        status: err instanceof ApiError ? err.status : 0,
      });
      return;
    }

    const merged = mergeItems(baseItems, indices, result.items);
    const uploaded = merged.filter((i) => i.status === "done").length;
    // A missing full frame only costs the IBV comparison (isOptionalUpload), so
    // it does not stop the scan; any other missing file does.
    const missingRequired = merged.some(
      (i) => i.status !== "done" && !isOptionalUpload(i.filename),
    );
    if (missingRequired) {
      setPhase({ kind: "incomplete", session, items: merged });
      return;
    }

    // Funnel step 5: every PUT landed. Fired before the completion trigger, so
    // a scan that uploads fine but fails to trigger is still distinguishable
    // from one that never finished uploading. Not fired at all while files are
    // missing, so the funnel never counts a partial upload as complete.
    emit("uploads_complete", session.scanID, { files: uploaded });

    setPhase({ kind: "triggering", session, items: merged });

    // Terminal completion signal: the footscan-server /process trigger.
    // Reusable invites first bind their pairs via hezo-ops's /notify (see
    // runCompletionTrigger). Both map onto the same ProcessTriggerResult so
    // DoneScreen renders either identically.
    const triggerResult = await fireCompletionTrigger(session, token);
    await settleAfterTrigger(session, merged, triggerResult);
  }

  // Re-upload exactly the files that aren't in S3 yet, then continue as if the
  // first run had succeeded. Each attempt runs the pipeline afresh, so it mints
  // new presigned URLs for those filenames, as the ones from the first attempt
  // may well have expired by the time the customer taps the button.
  //
  // Serves both recoverable failure screens: "incomplete" (some PUTs failed)
  // and "failed" (the presigned-uploads call itself blew up, so nothing went
  // up at all). Neither reloads the page: the capture blobs only exist in
  // memory, and a reload would make the customer shoot all six photos again
  // because one HTTP call went wrong.
  async function retryFailedUploads(): Promise<void> {
    if (phase.kind !== "incomplete" && phase.kind !== "failed") return;
    const token = tokenRef.current;
    if (!token) return;
    const { session, items } = phase;
    const indices = items.flatMap((it, i) => (it.status === "done" ? [] : [i]));
    if (indices.length === 0) return;
    const retried = items.map((it, i) =>
      indices.includes(i) ? { ...it, status: "pending" as const, errorMessage: undefined } : it,
    );
    setPhase({ kind: "running", session, items: retried });
    await uploadAndSettle(session, token, retried, indices);
  }

  // A sync "ok"/"error" trigger result is already terminal. An "accepted"
  // (202) one is not: the reconstruction is still running server-side, so
  // instead of claiming success we start polling for the real outcome
  // (see the "polling" effect below). The customer sees the finished screen
  // throughout either way; only its wording changes once the poll settles.
  async function settleAfterTrigger(
    session: ScanSession,
    items: UploadItem[],
    triggerResult: ProcessTriggerResult,
  ): Promise<void> {
    const plan = runPlanRef.current;
    const token = tokenRef.current;
    if (plan && token && triggerResult.kind !== "error") {
      const finished = runInfo(plan, runPairsRef.current);
      const advanced = await advanceRuns(plan, token);
      runPlanRef.current = advanced.plan;
      if (advanced.next.length > 0) {
        // The reconstruction of this run carries on server-side; nothing on
        // the phone is needed for it any more, so let the photos go before
        // the next run's camera and model fill the memory again.
        uploadInputsRef.current = [];
        // Straight on: the review button already said "continue to run k+1",
        // so the between page only shows while /start runs. Its button is
        // the fallback when that fails.
        const between: Extract<Phase, { kind: "between" }> = {
          kind: "between",
          customerId: session.customerId,
          flow: session.flow,
          finished,
          next: runInfo(advanced.plan, advanced.next),
          pairIds: advanced.next,
          starting: true,
        };
        setPhase(between);
        try {
          await openSession(token, between.pairIds, between.customerId, between.flow);
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            await resumeInvite();
            return;
          }
          setPhase({ ...between, starting: false, error: startErrorMessage(err, t) });
        }
        return;
      }
      setMorePairs(advanced.otherPairsOpen);
      setRunsSummary({
        runs: advanced.plan.required,
        pairs: advanced.plan.pairless ? [] : pickedPairsRef.current,
      });
    }
    if (triggerResult.kind === "accepted") {
      setPhase({
        kind: "polling",
        session,
        items,
        expectedSeconds: triggerResult.body.expectedSeconds,
      });
      return;
    }
    setPhase({ kind: "done", session, items, triggerResult });
  }

  // Write the capture to a file instead of S3 (dev capture). One JSON, because
  // iOS Safari drops all but the first of a download burst; unpacked by
  // tools/proto-ab/extract.py, which also reads the prototype harness's dumps —
  // so a prototype run and an SPA run land as identical filenames, ready to diff.
  //
  // With a capture-ingest token set (`?ingest=<token>`) the same JSON is POSTed
  // to the ingest instead, which spools it for the reconstruction worker — a
  // scan taken on a phone reaches HiDrive with no file handling at all. The
  // download stays the fallback: if the POST fails, the shots must not be lost.
  async function saveScanLocally(
    session: ScanSession,
    inputs: RunInput[],
    label?: string,
  ): Promise<void> {
    // The label is what pairs this run with the same foot captured in
    // tools/proto-ab: the worker publishes both under it on HiDrive.
    const cleanLabel = label ? normaliseCaptureLabel(label) : undefined;
    const bundle = await buildScanBundle(inputs, {
      at: new Date().toISOString(),
      scanID: session.scanID,
      version: APP_VERSION,
      ua: navigator.userAgent,
      ...(cleanLabel ? { label: cleanLabel } : {}),
    });
    const json = JSON.stringify(bundle, null, 2);
    const name = `spa-dev-${cleanLabel ? `${cleanLabel}-` : ""}${session.scanID}-${Date.now()}.json`;
    const ingestToken = captureIngestToken();

    if (ingestToken) {
      // Leave the capture UI before the (multi-second, ~10 MB) POST: it shows a
      // live "Upload" button, and a second press would spool the bundle twice.
      setPhase({ kind: "savedLocally", session, filename: null, ingest: { kind: "pending" } });
      const ingest = await postScanBundle(CAPTURE_INGEST_URL, ingestToken, json);
      if (ingest.kind === "spooled") {
        setPhase({ kind: "savedLocally", session, filename: null, ingest });
        return;
      }
      setPhase({ kind: "savedLocally", session, filename: downloadJson(name, json), ingest });
      return;
    }

    setPhase({ kind: "savedLocally", session, filename: downloadJson(name, json), ingest: null });
  }

  // Fire the terminal completion trigger for a session, folding any throw into a
  // ProcessTriggerResult error so callers never have to try/catch. Shared by the
  // initial upload path and the DoneScreen "retry" action — by the time either
  // runs the files are already in S3, so a retry re-fires only this trigger.
  async function fireCompletionTrigger(
    session: ScanSession,
    token: string,
  ): Promise<ProcessTriggerResult> {
    const result = await runCompletionTrigger(session, token);
    // Funnel step 6, the only one that carries success/failure. The whitelist
    // stays at six values, the outcome rides in `detail`. Reported here rather
    // than at the call site so the DoneScreen retry re-reports it too (the
    // server overwrites `detail`, so the last attempt wins).
    //
    // `errors` carries every issue code the 422 listed, not just the first —
    // codes are a stable, bounded set (see ScanIssue), not free-form client
    // text, so they're safe to report in full.
    emit(
      "process_triggered",
      session.scanID,
      result.kind === "error"
        ? {
            ok: false,
            status: result.status,
            ...(result.customerIssues?.length
              ? { errors: result.customerIssues.map((i) => i.error) }
              : {}),
          }
        : { ok: true },
    );
    return result;
  }

  async function runCompletionTrigger(
    session: ScanSession,
    token: string,
  ): Promise<ProcessTriggerResult> {
    try {
      if (!reusableRef.current) {
        return await triggerScanProcess(
          HEZO_OPS_BASE_URL,
          token,
          session.scanID,
          session.customerId,
        );
      }
      // Reusable: /notify strictly before /process. footscan-server's SQS
      // notify after the reconstruction stamps the session processed, so a
      // later /notify would be refused with 410 and the pairs left unbound.
      if (notifiedScanRef.current !== session.scanID) {
        const notified = await notifyScanSession(HEZO_OPS_BASE_URL, token, session.scanID);
        // 410 already_processed: an earlier /notify landed but its answer got
        // lost. The pairs are bound, so carry on with /process.
        const replayed = notified.kind === "error" && notified.status === 410;
        if (notified.kind === "error" && !replayed) return notified;
        notifiedScanRef.current = session.scanID;
      }
      // hezo-ops builds before #1684 hand out no session token, and the invite
      // token would get a 401 from footscan-server. Keep the old notify-only
      // outcome there instead of showing the customer an error.
      if (!scanTokenRef.current) {
        return { kind: "ok", status: 200, body: { ok: true, scanID: session.scanID } };
      }
      return await triggerScanProcess(
        HEZO_OPS_BASE_URL,
        scanTokenRef.current,
        session.scanID,
        session.customerId,
      );
    } catch (err) {
      return {
        kind: "error",
        status: 0,
        body: `completion trigger threw: ${(err as Error).message}`,
      };
    }
  }

  // Re-fire the completion trigger from the DoneScreen error state, without
  // re-uploading (files are already in S3). Brief bounce through "triggering"
  // for the spinner, then back to "done" with the fresh result.
  async function retryCompletionTrigger(): Promise<void> {
    if (phase.kind !== "done") return;
    const token = tokenRef.current;
    if (!token) return;
    const { session, items } = phase;
    setPhase({ kind: "triggering", session, items });
    const triggerResult = await fireCompletionTrigger(session, token);
    await settleAfterTrigger(session, items, triggerResult);
  }

  // The HEZO wordmark stays visible on every screen. For the normal centered
  // screens it's the sticky header below; the full-bleed capture viewfinder
  // (fixed inset-0) covers that header, so it carries its own logo in its top
  // overlay (see CaptureViewfinder). `immersive` now only drops the debug
  // footer so it can't poke out under the viewfinder.
  const immersive = phase.kind === "ready" && phase.session.flow === "self-capture";

  return (
    <AppShell phase={phase.kind}>
      {phase.kind === "landing" && <LandingScreen />}
      {phase.kind === "loading" && <LoadingScreen />}
      {phase.kind === "invalid" && (
        <InvalidScreen reason={phase.reason} detail={phase.detail} />
      )}
      {phase.kind === "allDone" && <AllDoneScreen />}
      {phase.kind === "picking" && (
        <PairPicker
          pairs={phase.picking.pairs}
          starting={phase.picking.starting}
          errorMessage={phase.picking.error}
          onStart={(pairIds) => void startPicking(pairIds)}
        />
      )}
      {phase.kind === "between" && (
        <BetweenRunsScreen
          finished={phase.finished}
          next={phase.next}
          starting={phase.starting}
          errorMessage={phase.error}
          onContinue={() => void startNextRun()}
        />
      )}
      {phase.kind === "ready" && phase.session.flow === "volumental" && (
        <VolumentalReadyScreen
          session={phase.session}
          files={volumentalFiles}
          setFiles={setVolumentalFiles}
          onStart={() => startVolumentalUpload(volumentalFiles)}
        />
      )}
      {phase.kind === "ready" && phase.session.flow === "self-capture" && (
        <CaptureFlow
          // A fresh mount per run: every run starts from an empty capture state.
          key={phase.session.scanID}
          session={phase.session}
          run={run}
          consented={run && run.index > 1 ? runConsent() : null}
          startMode={run && run.index > 1 ? (cameraStartedRef.current ? "auto" : "tap") : undefined}
          onConsent={(paperFormat) => {
            consentRef.current = { paperFormat };
            if (runPlanRef.current && tokenRef.current) {
              storeRunConsent(tokenRef.current, paperFormat);
            }
          }}
          onComplete={(inputs, label) => void runScanUpload(inputs, label)}
          onSimulate={(label) => void startSelfCaptureUpload(label)}
          onFunnelEvent={(type, detail) => {
            if (type === "capture_started") cameraStartedRef.current = true;
            emit(type, phase.session.scanID, detail);
          }}
        />
      )}
      {phase.kind === "running" && (
        <RunningScreen session={phase.session} items={phase.items} run={run} />
      )}
      {phase.kind === "incomplete" && (
        <IncompleteScreen
          session={phase.session}
          items={phase.items}
          onRetry={() => void retryFailedUploads()}
          run={run}
        />
      )}
      {phase.kind === "triggering" && (
        <TriggeringScreen session={phase.session} items={phase.items} run={run} />
      )}
      {/* Same screen as "done": the customer is finished either way, and the
          poll only refines what the screen already says. */}
      {phase.kind === "polling" && (
        <DoneScreen
          session={phase.session}
          items={phase.items}
          triggerResult={{
            kind: "accepted",
            status: 202,
            body: {
              ok: true,
              scanID: phase.session.scanID,
              expectedSeconds: phase.expectedSeconds,
            },
          }}
          onRetryTrigger={() => void retryCompletionTrigger()}
          onMorePairs={morePairs ? () => void resumeInvite() : undefined}
          onRepeatRun={runPlanRef.current ? () => void resumeInvite() : undefined}
          runsSummary={runsSummary ?? undefined}
        />
      )}
      {phase.kind === "done" && (
        <DoneScreen
          session={phase.session}
          items={phase.items}
          triggerResult={phase.triggerResult}
          onRetryTrigger={() => void retryCompletionTrigger()}
          onMorePairs={morePairs ? () => void resumeInvite() : undefined}
          onRepeatRun={runPlanRef.current ? () => void resumeInvite() : undefined}
          runsSummary={runsSummary ?? undefined}
        />
      )}
      {phase.kind === "savedLocally" && (
        <SavedLocallyScreen
          session={phase.session}
          filename={phase.filename}
          ingest={phase.ingest}
        />
      )}
      {phase.kind === "failed" && (
        <FailedScreen
          session={phase.session}
          items={phase.items}
          errorMessage={phase.errorMessage}
          status={phase.status}
          onRetry={() => void retryFailedUploads()}
        />
      )}
      {/* Operator debug only — the backend origin is noise for real customers. */}
      {DEBUG && !immersive && (
        <footer className="border-t border-white/10 px-4 py-3 text-center text-xs text-slate-500">
          hezo-ops origin: <code className="font-mono">{HEZO_OPS_BASE_URL}</code>
        </footer>
      )}
    </AppShell>
  );
}

/**
 * Header + main column every phase renders into. Extracted so the copy preview
 * (`src/preview/`) can show a screen inside the real chrome instead of an
 * approximation of it: the tagline and the language switcher are editable copy
 * too, and a rebuilt header would drift from this one.
 */
export function AppShell({
  phase,
  children,
  footer,
}: {
  /** Goes on `data-phase`, which the tests and the preview select on. */
  phase: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-slate-900/60 px-4 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center gap-3">
          <HezoLogo className="h-7 text-white" />
          <span className="text-sm text-slate-400">{t("app.tagline")}</span>
          <div className="ml-auto">
            <LanguageSwitcher variant="compact" />
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6">
        <div className="mx-auto max-w-md" data-testid="phase-root" data-phase={phase}>
          {children}
        </div>
      </main>
      {footer}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

// Customer-facing scan reference. Shown on every status screen so the customer
// always has the 6-char ID to quote to support — distinct from the DEBUG blocks,
// which carry operator detail (S3 keys, customerId, trigger internals).
function ScanIdLine({ session }: { session: ScanSession }): React.ReactElement {
  const { t } = useI18n();
  return (
    <p className="text-xs text-slate-500" data-testid="scan-id">
      {t("app.scanId")}:{" "}
      <code className="font-mono tracking-wider text-slate-300">{session.scanID}</code>
    </p>
  );
}

// Prominent "don't leave" banner shown while the upload + process-trigger run.
// Pairs with the beforeunload guard (which only catches hard tab-close/navigate)
// to make the "keep this open" expectation explicit to the customer.
function StayOnPageNotice(): React.ReactElement {
  const { t } = useI18n();
  return (
    <div
      className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100"
      data-testid="stay-on-page"
    >
      <span aria-hidden="true" className="leading-5">
        ⚠️
      </span>
      <span>{t("status.stayOnPage")}</span>
    </div>
  );
}

// Big, copy-friendly scan-ID block for the terminal done screen. The subtle
// ScanIdLine is right for the in-progress screens; on the done screen the ID is
// the one thing the customer should keep, so it gets its own prominent card.
function ScanReference({ session }: { session: ScanSession }): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className={`${PANEL} text-center`} data-testid="scan-reference">
      <p className="text-xs uppercase tracking-wide text-slate-400">{t("app.scanId")}</p>
      <p className="mt-1 font-mono text-2xl font-semibold tracking-[0.25em] text-white">
        {session.scanID}
      </p>
      <p className="mt-2 text-xs text-slate-400">{t("status.keepReference")}</p>
    </div>
  );
}

/** What a finished multi-run round did, for the done screen. `pairs` is
 *  empty for a pairless invite (walk-in, campaign, complaint). */
export interface RunsSummary {
  runs: number;
  pairs: ScanPair[];
}

// Replaces the scan-ID card after the last run of a multi-run invite: the
// visit produced 2N scans (our own and the IBV one per run), so one scanID
// would only confuse. Says how many runs are in and for which pairs.
function RunsSummaryCard({ summary }: { summary: RunsSummary }): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className={`${PANEL} text-center`} data-testid="runs-summary">
      <p className="font-semibold text-white">{t("runs.summaryTitle", { runs: summary.runs })}</p>
      {summary.pairs.length > 0 && (
        <>
          <p className="mt-2 text-xs uppercase tracking-wide text-slate-400">
            {t("runs.summaryPairs")}
          </p>
          <ul className="mt-1 space-y-1 text-sm text-slate-200">
            {summary.pairs.map((p) => (
              <li key={p.id} data-testid={`runs-summary-pair-${p.id}`}>
                {[
                  p.position.trim() ? t("picker.pairLabel", { n: p.position.trim() }) : "",
                  p.shoeType.trim(),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Terminal screen for dev capture: the shots went to a file, not to S3.
 *
 * Deliberately blunt about that — a tester who mistakes this for a real upload
 * would go looking in the bucket for bytes that were never sent. Strings are
 * hardcoded English rather than i18n keys: this is unreachable outside a
 * dev-capture build, and adding keys would oblige translations in all five
 * language blocks for a screen no customer can ever see.
 */
function SavedLocallyScreen({
  session,
  filename,
  ingest,
}: {
  session: ScanSession;
  filename: string | null;
  ingest: IngestOutcome | null;
}): React.ReactElement {
  if (ingest?.kind === "pending") {
    return (
      <div className="space-y-5" data-testid="saved-locally-screen" data-ingest="pending">
        <section className={PANEL}>
          <h2 className="text-lg font-semibold text-white">Sending to capture-ingest…</h2>
          <p className="mt-2 text-sm text-slate-300">
            Dev capture ({session.scanID}) — keep this page open until it lands.
          </p>
        </section>
      </div>
    );
  }

  if (ingest?.kind === "spooled") {
    return (
      <div className="space-y-5" data-testid="saved-locally-screen" data-ingest="spooled">
        <section className={PANEL}>
          <h2 className="text-lg font-semibold text-white">Sent to capture-ingest</h2>
          <p className="mt-2 text-sm text-slate-300">
            Dev capture ({session.scanID}) — spooled for reconstruction. Nothing went to
            S3 and no customer-facing processing was triggered. No file on this device.
          </p>
          <p
            className="mt-3 font-mono text-xs text-emerald-300"
            data-testid="ingest-spooled"
          >
            {ingest.spooled} · {Math.round(ingest.bytes / 1024)} KB
          </p>
        </section>
        <section className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
          <p>The worker runs all three solvers and publishes to HiDrive under</p>
          <pre className="mt-2 overflow-x-auto font-mono text-xs text-slate-200">
            Test_Footscan_Import/capture-ab/
          </pre>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="saved-locally-screen">
      <section className={PANEL}>
        <h2 className="text-lg font-semibold text-white">Saved locally</h2>
        <p className="mt-2 text-sm text-slate-300">
          Dev capture ({session.scanID}) — nothing was uploaded and no processing was
          triggered. The shots and <code>capture-meta.json</code> are in the downloaded
          file.
        </p>
        {ingest?.kind === "error" && (
          <p
            className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-100"
            data-testid="ingest-error"
          >
            capture-ingest refused it ({ingest.detail}) — fell back to the download.
          </p>
        )}
        {filename ? (
          <p className="mt-3 font-mono text-xs text-emerald-300" data-testid="saved-filename">
            {filename}
          </p>
        ) : (
          <p className="mt-3 text-sm text-amber-300">
            The download was blocked by the browser — re-run with downloads allowed.
          </p>
        )}
      </section>
      <section className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
        <p>Unpack it into the upload filenames:</p>
        <pre className="mt-2 overflow-x-auto font-mono text-xs text-slate-200">
          tools/proto-ab/extract.py {filename ?? "spa-dev-*.json"} -o out/spa
        </pre>
      </section>
    </div>
  );
}

export function LandingScreen(): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-5" data-testid="landing-screen">
      <section className={PANEL}>
        <h2 className="text-lg font-semibold text-white">{t("landing.title")}</h2>
        <p className="mt-2 text-sm text-slate-300">{t("landing.intro")}</p>
      </section>

      <section className="rounded-xl border border-sky-500/25 bg-sky-500/10 p-4 text-sm text-sky-100">
        <h3 className="font-semibold">{t("landing.lookingTitle")}</h3>
        <p className="mt-2 text-sky-100/90">{t("landing.lookingBody")}</p>
        <p className="mt-3 text-sky-100/90">
          {t("landing.cantFind", { email: "support@hezo-cycling.com" })
            .split("support@hezo-cycling.com")
            .flatMap((seg, i) =>
              i === 0
                ? [seg]
                : [
                    <a
                      key="mail"
                      href="mailto:support@hezo-cycling.com"
                      className="font-medium underline"
                    >
                      support@hezo-cycling.com
                    </a>,
                    seg,
                  ],
            )}
        </p>
      </section>

      <div className="text-center">
        <a
          href="https://www.hezo-cycling.com/"
          className="inline-block rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-white/5"
        >
          {t("landing.visit")}
        </a>
      </div>
    </div>
  );
}

export function LoadingScreen(): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="py-12 text-center">
      <div className="inline-block animate-pulse text-slate-400">{t("app.loading")}</div>
    </div>
  );
}

export function InvalidScreen({
  reason,
  detail,
}: {
  reason: InvalidReason;
  detail?: string;
}): React.ReactElement {
  const { t } = useI18n();
  const text =
    reason === "expired"
      ? t("invalid.expired")
      : reason === "notFound"
        ? t("invalid.notFound")
        : reason === "processed"
          ? t("invalid.processed")
          : t("invalid.unreachable", { message: detail ?? "" });
  return (
    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
      <h2 className="font-semibold text-rose-200">{t("invalid.title")}</h2>
      <p className="mt-2 text-sm text-rose-100/90">{text}</p>
    </div>
  );
}

// Reusable invite where every pair has already been scanned (GET done:true).
// Re-opening the link lands here instead of an empty picker.
export function AllDoneScreen(): React.ReactElement {
  const { t } = useI18n();
  return (
    <div
      className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
      data-testid="all-done-screen"
    >
      <h2 className="font-semibold text-emerald-200">{t("allDone.title")}</h2>
      <p className="mt-2 text-sm text-emerald-100/90">{t("allDone.body")}</p>
    </div>
  );
}

export interface VolumentalFiles {
  left: File | null;
  right: File | null;
}

export function VolumentalReadyScreen({
  session,
  files,
  setFiles,
  onStart,
  showBadType = false,
}: {
  session: ScanSession;
  files: VolumentalFiles;
  setFiles: (files: VolumentalFiles) => void;
  onStart: () => void;
  /** Copy preview only: start with the wrong-file-type hint on the left field. */
  showBadType?: boolean;
}): React.ReactElement {
  const { t } = useI18n();
  const ready = files.left !== null && files.right !== null;
  return (
    <div className="space-y-5">
      <section>
        <h2 className="text-lg font-semibold text-white">{t("volumental.title")}</h2>
        <ScanIdLine session={session} />
        {DEBUG && (
          <p className="text-sm text-slate-400">
            customer:{" "}
            <code className="font-mono text-xs">{session.customerId.slice(0, 8)}…</code>
          </p>
        )}
      </section>

      <section className={`${PANEL} space-y-3`}>
        <h3 className="text-sm font-semibold text-slate-200">{t("volumental.filesTitle")}</h3>
        <p className="text-xs text-slate-400">{t("volumental.intro")}</p>
        {(["left", "right"] as const).map((side) => (
          <VolumentalFileField
            key={side}
            side={side}
            label={t(side === "left" ? "volumental.left" : "volumental.right")}
            badType={t("volumental.badType")}
            file={files[side]}
            initiallyRejected={showBadType && side === "left"}
            onChange={(file) => setFiles({ ...files, [side]: file })}
          />
        ))}
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready}
        data-testid="volumental-upload-button"
        className={PRIMARY_BTN}
      >
        {t("volumental.upload")}
      </button>
    </div>
  );
}

function VolumentalFileField({
  side,
  label,
  badType,
  file,
  initiallyRejected,
  onChange,
}: {
  side: VolumentalSide;
  label: string;
  badType: string;
  file: File | null;
  initiallyRejected: boolean;
  onChange: (file: File | null) => void;
}): React.ReactElement {
  const [rejected, setRejected] = useState(initiallyRejected);
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-200">
        {label}
        <input
          type="file"
          accept={VOLUMENTAL_ACCEPT}
          onChange={(e) => {
            const picked = e.target.files?.[0] ?? null;
            const ok = picked !== null && volumentalExtension(picked) !== null;
            setRejected(picked !== null && !ok);
            onChange(ok ? picked : null);
          }}
          className="mt-1 block w-full text-xs text-slate-300"
          data-testid={`volumental-file-${side}`}
        />
      </label>
      {rejected && (
        <p className="text-xs text-red-300" data-testid={`volumental-file-${side}-error`}>
          {badType}
        </p>
      )}
      {file && (
        <p className="flex justify-between text-xs text-slate-300">
          <code className="font-mono" data-testid={`volumental-file-${side}-target`}>
            {volumentalTargetName(side, file)}
          </code>
          <span className="tabular-nums text-slate-500">{Math.ceil(file.size / 1024)} KB</span>
        </p>
      )}
    </div>
  );
}

function UploadProgress({ items }: { items: UploadItem[] }): React.ReactElement {
  const { t } = useI18n();
  const done = items.filter((i) => i.status === "done").length;
  const failed = items.filter((i) => i.status === "error").length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-300">
        {t("status.uploadProgress", { done, total: items.length })}
        {failed > 0 && <span className="text-rose-400"> · {failed}</span>}
      </p>
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-white transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// "Durchlauf k von N" above the upload screens of a multi-run invite.
function RunBadge({ run }: { run: RunInfo }): React.ReactElement {
  const { t } = useI18n();
  return (
    <p
      className="text-xs font-semibold uppercase tracking-wide text-amber-200"
      data-testid="run-badge"
    >
      {t("runs.badge", { run: run.index, runs: run.total })}
    </p>
  );
}

// Between two runs of a multi-run invite. The run just finished is uploaded
// and its reconstruction runs server-side; the customer only has to set up
// again and start the next one. Deliberately no "done" and no "close the
// page": the runs are mandatory, and a link reopened later only resumes here
// as the exception (via the picker).
export function BetweenRunsScreen({
  finished,
  next,
  starting,
  errorMessage,
  onContinue,
}: {
  finished: RunInfo;
  next: RunInfo;
  starting: boolean;
  errorMessage?: string;
  onContinue: () => void;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-4" data-testid="between-runs-screen">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
        <h2 className="font-semibold text-emerald-200">
          {t("runs.betweenTitle", { run: finished.index, runs: finished.total })}
        </h2>
        <p className="mt-1 text-sm text-emerald-100/90">
          {t("runs.betweenWhy", { runs: finished.total })}
        </p>
      </div>
      <section className={PANEL}>
        <h3 className="font-semibold text-white">{t("runs.betweenSetupTitle")}</h3>
        <p className="mt-2 text-sm text-slate-300">{t("runs.betweenSetupBody")}</p>
      </section>
      {errorMessage && (
        <div
          className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-100"
          data-testid="between-runs-error"
        >
          {errorMessage}
        </div>
      )}
      <button
        type="button"
        onClick={onContinue}
        disabled={starting}
        className={PRIMARY_BTN}
        data-testid="next-run-button"
      >
        {starting
          ? t("runs.betweenStarting")
          : t("runs.betweenContinue", { run: next.index, runs: next.total })}
      </button>
    </div>
  );
}

export function RunningScreen({
  session,
  items,
  run = null,
}: {
  session: ScanSession;
  items: UploadItem[];
  run?: RunInfo | null;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      {run && <RunBadge run={run} />}
      <h2 className="text-lg font-semibold text-white">{t("status.uploadingTitle")}</h2>
      <p className="text-sm text-slate-300">{t("status.uploadingBody")}</p>
      <UploadProgress items={items} />
      <StayOnPageNotice />
      {!run && <ScanIdLine session={session} />}
      {DEBUG && <UploadList items={items} />}
    </div>
  );
}

// Some files never made it into S3. The scan is not lost (the blobs are still
// in memory), so this screen's only job is to get the customer to tap retry,
// which re-PUTs exactly those files and then continues into the trigger. No
// trigger has fired at this point, deliberately: a short prefix would be
// processed as if it were the whole scan.
export function IncompleteScreen({
  session,
  items,
  onRetry,
  run = null,
}: {
  session: ScanSession;
  items: UploadItem[];
  onRetry: () => void;
  /** A run of a multi-run invite: its scanID is one of 2N, so none is shown. */
  run?: RunInfo | null;
}): React.ReactElement {
  const { t } = useI18n();
  // No file count and no progress bar here on purpose: "3 of 7 files" reads as
  // if single photos could be swapped, while a retake is always a whole foot.
  // The retry only re-sends bytes that already exist; the customer just needs
  // to finish the upload.
  return (
    <div className="space-y-4" data-testid="incomplete-screen">
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
        <h2 className="font-semibold text-amber-200">{t("status.uploadIncompleteTitle")}</h2>
        <p className="mt-1 text-sm text-amber-100/90">
          {t("status.uploadIncompleteBody")}
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className={PRIMARY_BTN}
        data-testid="retry-failed-uploads"
      >
        {t("status.uploadIncompleteRetry")}
      </button>
      <StayOnPageNotice />
      {!run && <ScanIdLine session={session} />}
      {DEBUG && <UploadList items={items} />}
    </div>
  );
}

export function TriggeringScreen({
  session,
  items,
  run = null,
}: {
  session: ScanSession;
  items: UploadItem[];
  run?: RunInfo | null;
}): React.ReactElement {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      {run && <RunBadge run={run} />}
      <h2 className="text-lg font-semibold text-white">{t("status.startingTitle")}</h2>
      <p className="text-sm text-slate-300">{t("status.startingBody")}</p>
      {/* Indeterminate bar — the trigger is a single request with no per-item
          progress, so a moving stripe reads better than a frozen 100% bar. */}
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full w-1/3 animate-pulse rounded-full bg-white/70" />
      </div>
      <StayOnPageNotice />
      {!run && <ScanIdLine session={session} />}
      {DEBUG && (
        <div className="animate-pulse text-sm text-slate-500">
          All {items.length} files in S3. Triggering footscan-server for scanID{" "}
          <code className="font-mono text-xs">{session.scanID}</code>…
        </div>
      )}
    </div>
  );
}

/** The title + body copy picked for one DoneScreen outcome box. */
interface BoxCopy {
  border: string;
  title: string;
  body: string;
  titleKey: TransKey;
  bodyKey: TransKey;
}

// The customer's finish line, shown from the moment the trigger is through —
// during the poll as well as after it. Everything the phone owes us is in S3,
// reconstruction takes minutes, and hezo-ops mails the outcome either way
// (scan-confirmation on success, scan-retake when the capture was the problem),
// so there is no waiting room and no progress bar: the screen says the scan is
// done and the page may be closed. The poll keeps running behind it purely as a
// courtesy — a customer who stays gets the real result in place, including the
// `internal_error` case that sends no mail by design.
export function DoneScreen({
  session,
  items,
  triggerResult,
  onRetryTrigger,
  onMorePairs,
  onRepeatRun,
  runsSummary,
}: {
  session: ScanSession;
  items: UploadItem[];
  triggerResult: ProcessTriggerResult;
  onRetryTrigger: () => void;
  /** After the last run of a multi-run invite: the done screen sums the runs
   *  up instead of showing the last run's scanID, which is one of 2N scans. */
  runsSummary?: RunsSummary;
  /** Other pairs of the invite still need their runs: offer the picker
   *  instead of "close the page". */
  onMorePairs?: () => void;
  /** Multi-run invite: a failed run does not count, so a dead end offers to
   *  repeat it (back to the picker with fresh counts) instead of "start over". */
  onRepeatRun?: () => void;
}): React.ReactElement {
  const { t } = useI18n();
  const triggerOk = triggerResult.kind === "ok" || triggerResult.kind === "accepted";
  // Only offer "retry" for a trigger failure that might actually succeed on a
  // second attempt — a 4xx validation error is deterministic against the same
  // already-uploaded photos, so retrying it would just fail identically.
  const triggerRecoverable =
    triggerResult.kind === "error" && isRecoverableTriggerError(triggerResult.status);
  // Which of the two failure stages we're in. A trigger that never got through
  // left processing unstarted; a failure the status poll reported means
  // processing did start and then failed. Same red box, different sentence.
  const processingFailed = triggerResult.kind === "error" && triggerResult.stage === "processing";
  // Success has two flavours. Until the poll settles we only know the photos
  // are in and the trigger is through; once the server reports the
  // reconstruction ran without errors, the copy may say so. It is still not a
  // finished fitting — a human reviews the result — so it stops at
  // "processed, we'll check it" and never claims a finished foot model.
  const processed = triggerResult.kind === "ok" && triggerResult.processed === true;

  // Two terminal outcomes. A missing file can no longer land here (the upload
  // phase stops at "incomplete" and never triggers), so the only failure this
  // screen still reports is the trigger itself, which IS recoverable: the files
  // are already in S3, so the retry re-hits only the trigger.
  const tone: "ok" | "error" = triggerOk ? "ok" : "error";

  const boxes: Record<typeof tone, BoxCopy> = {
    ok: {
      border: "border-emerald-500/30 bg-emerald-500/10",
      title: "text-emerald-200",
      body: "text-emerald-100/90",
      titleKey: "status.doneTitle",
      bodyKey: processed ? "status.doneProcessedBody" : "status.doneBody",
    },
    error: {
      border: "border-rose-500/30 bg-rose-500/10",
      title: "text-rose-200",
      body: "text-rose-100/90",
      titleKey: processingFailed ? "status.processingFailedTitle" : "status.submitIncompleteTitle",
      // The body only points at the retry button when that button actually
      // renders below: both are gated on `triggerRecoverable`, so a customer
      // can no longer be told to tap something that isn't there and then, one
      // line down, to start a whole new scan instead.
      bodyKey: processingFailed
        ? triggerRecoverable
          ? "status.processingFailedRetryBody"
          : "status.processingFailedBody"
        : triggerRecoverable
          ? "status.submitIncompleteBody"
          : "status.submitIncompleteNoRetryBody",
    },
  };
  const box = boxes[tone];

  // Every problem the server found, in capture order (left before right,
  // photo 1 before 3) — not just the first, so a customer who forgot to go
  // barefoot AND cut off the sheet sees both before repeating the scan.
  const issues: ScanIssue[] | undefined =
    tone === "error" && triggerResult.kind === "error" ? triggerResult.customerIssues : undefined;

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${box.border}`}>
        <h2 className={`font-semibold ${box.title}`}>{t(box.titleKey)}</h2>
        {issues && issues.length > 1 ? (
          <ul className={`mt-1 list-disc space-y-1 pl-5 text-sm ${box.body}`}>
            {issues.map((issue, i) => (
              <li key={i}>{issue.message}</li>
            ))}
          </ul>
        ) : (
          <p className={`mt-1 text-sm ${box.body}`}>{issues?.[0]?.message ?? t(box.bodyKey)}</p>
        )}
      </div>

      {tone === "ok" &&
        (onMorePairs ? (
          <div className="space-y-3" data-testid="more-pairs">
            <p className="text-sm text-slate-300">{t("runs.morePairsBody")}</p>
            <button type="button" onClick={onMorePairs} className={PRIMARY_BTN}>
              {t("runs.morePairsButton")}
            </button>
          </div>
        ) : (
          <p className="text-sm text-emerald-200" data-testid="can-close">
            {t("status.canClose")}
          </p>
        ))}

      {tone === "error" &&
        (triggerRecoverable ? (
          <button type="button" onClick={onRetryTrigger} className={SECONDARY_BTN}>
            {t("status.failedRetry")}
          </button>
        ) : onRepeatRun ? (
          <button
            type="button"
            onClick={onRepeatRun}
            className={PRIMARY_BTN}
            data-testid="repeat-run"
          >
            {t("runs.repeatRun")}
          </button>
        ) : (
          <p className="text-sm text-rose-100/80">{t("status.submitIncompleteRestart")}</p>
        ))}

      {tone === "ok" && runsSummary ? (
        <RunsSummaryCard summary={runsSummary} />
      ) : (
        <ScanReference session={session} />
      )}

      {DEBUG && (
        <>
          <p className="text-xs text-slate-500">
            {items.filter((i) => i.status === "done").length}/{items.length} files under{" "}
            <code className="font-mono">
              hezo-scans/{session.customerId.slice(0, 8)}…/{session.scanID}/
            </code>
          </p>
          <TriggerResultBlock result={triggerResult} session={session} />
          <UploadList items={items} />
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer">debug: session JSON</summary>
            <pre className="mt-2 overflow-auto rounded bg-white/5 p-2">
              {JSON.stringify(session, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}

function TriggerResultBlock({
  result,
  session,
}: {
  result: ProcessTriggerResult;
  session: ScanSession;
}): React.ReactElement {
  const opsLink = `${HEZO_OPS_BASE_URL}/orders?customer=${encodeURIComponent(session.customerId)}`;
  if (result.kind === "ok") {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-100">
        <p className="font-semibold">Processing complete (sync 200)</p>
        {typeof result.body.outputCount === "number" && (
          <p className="mt-1">Output count: {result.body.outputCount}.</p>
        )}
        <p className="mt-2">
          <a href={opsLink} target="_blank" rel="noreferrer" className="underline">
            Show in HEZO OPS →
          </a>
        </p>
      </div>
    );
  }
  if (result.kind === "accepted") {
    return (
      <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-100">
        <p className="font-semibold">Processing started (async 202)</p>
        <p className="mt-1">
          Background processing kicked off
          {result.body.expectedSeconds > 0 ? ` (~${result.body.expectedSeconds}s)` : ""}.
        </p>
        <p className="mt-2">
          <a href={opsLink} target="_blank" rel="noreferrer" className="underline">
            Show in HEZO OPS →
          </a>
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-100">
      <p className="font-semibold">Process trigger failed: {result.status}</p>
      <p className="mt-1 break-words">{result.body}</p>
    </div>
  );
}

// A step before the PUTs blew up, in practice POST /presigned-uploads. The
// photos are untouched in memory, so this is not the end of the scan: a
// transient fault gets a retry that re-runs the upload with the same blobs. An
// expired or revoked link is the one case a retry cannot fix, and it gets its
// own sentence instead of a text about photos that never left the phone.
export function FailedScreen({
  session,
  items,
  errorMessage,
  status,
  onRetry,
}: {
  session: ScanSession;
  items: UploadItem[];
  errorMessage: string;
  status: number;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useI18n();
  const expired = status === 401 || status === 410;
  const recoverable = !expired && isRecoverableTriggerError(status);
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
        <h2 className="font-semibold text-rose-200">
          {t(expired ? "status.failedExpiredTitle" : "status.failedTitle")}
        </h2>
        <p className="mt-1 text-sm text-rose-100/90" data-testid="failed-body">
          {t(
            expired
              ? "status.failedExpiredBody"
              : recoverable
                ? "status.failedRetryBody"
                : "status.failedNoRetryBody",
          )}
        </p>
        <p className="mt-3 text-xs text-rose-200/80">
          {t("app.scanId")}:{" "}
          <code className="font-mono tracking-wider text-rose-100">{session.scanID}</code>
        </p>
        {DEBUG && (
          <p className="mt-2 break-words text-xs text-rose-200/80">{errorMessage}</p>
        )}
      </div>
      {DEBUG && items.length > 0 && <UploadList items={items} />}
      {recoverable && (
        <button
          type="button"
          onClick={onRetry}
          className={PRIMARY_BTN}
          data-testid="retry-failed-uploads"
        >
          {t("status.failedRetry")}
        </button>
      )}
    </div>
  );
}
